/* eslint-disable no-undef */
import { isDev } from '../../config'
import HttpClient from './HttpClient'

/**
 * Backend api base url of popety
 * @namespace Config
 */
export const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL

/**
 * REST Client that use native fetch to connect with the backend API.
 *
 * @namespace Services
 * @see https://developer.mozilla.org/fr/docs/Web/API/Fetch_API/Using_Fetch
 *
 * @example <caption>Usage</caption>
 * const client = new HttpClient()
 * const { data, errors } = await client.get('/users')
 * client.post('/users', userInput)
 * client.delete('/users/1')
 */
class ApiClient extends HttpClient {
  constructor(baseURL = API_BASE_URL) {
    super({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        locale: localStorage.getItem('i18nextLng') || 'en',
      },
      interceptors: {
        request() {
          try {
            const accessToken = localStorage.getItem('jwt')

            if (accessToken) {
              return { headers: { Authorization: `Bearer ${accessToken}` } }
            }
            const twoFAToken = localStorage.getItem('twoFAToken')

            if (twoFAToken) {
              return { headers: { Authorization: `Bearer ${twoFAToken}` } }
            }
          } catch (error) {
            console.error(error)
          }
        },
        async response(res: Response) {
          try {
            // Check if response has content before trying to parse JSON
            const contentType = res.headers.get('content-type')
            const hasJsonContent =
              contentType && contentType.includes('application/json')
            const hasContent = res.status !== 204 && res.status !== 304

            let payload = null
            if (hasJsonContent && hasContent) {
              payload = await res.json()
            }

            if (res.ok) return { ...res, data: payload }

            // Only disconnect user for token-related 401 errors (invalid/expired token)
            // Other 401 errors (missing permissions, etc.) should not disconnect
            const isTokenError =
              res.status === 401 &&
              payload?.error?.message &&
              (payload.error.message === 'Invalid token' ||
                payload.error.message === 'No token provided')

            if (
              isTokenError &&
              !window.location.pathname.includes('2fa') &&
              !window.location.pathname.includes('login')
            ) {
              // logout
              localStorage.removeItem('jwt')
              localStorage.removeItem('user')

              const { href } = window.location

              const from = href.substring(
                href.indexOf(window.location.pathname) - 1
              )

              sessionStorage.setItem('from', from)
              // Set the reason for session expiration (must be after any sessionStorage.clear calls)
              sessionStorage.setItem('session_expired_reason', 'expired')
              window.location.pathname = `/login`
            }

            // Format backend error to be displayed on the ui
            let errors = payload?.errors ||
              payload?.error ||
              payload || {
                message: `Request failed with status ${res.status}`,
              }

            errors = Array.isArray(errors) ? errors : [errors]

            if (isDev) {
              console.error(errors)
            }

            errors = errors.map((error: any) => {
              if (Array.isArray(error.message)) {
                return {
                  ...error,
                  ...error.message[0],
                  message: error.message[0]?.msg || error.message[0]?.message,
                }
              }

              return error
            })

            return { ...res, errors: Array.isArray(errors) ? errors : [errors] }
          } catch (error) {
            console.error(error)

            return { errors: [error] }
          }
        },
      },
    })
  }
}

const getUserId = () => {
  return localStorage.user && String(JSON.parse(localStorage.user).id)
}

const getCompanyId = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '').company_id
  } catch (error) {
    return error
  }
}

const getCompanyTemplateEmailAddress = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '')
      .company_template_email_address
  } catch (error) {
    return error
  }
}

export { getUserId, getCompanyId, getCompanyTemplateEmailAddress }

export default ApiClient
